import * as React from "react";
import {
  Box,
  Checkbox,
  Column,
  Columns,
  Heading,
  Hidden,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { Field, FieldProps, FormikProps, useFormikContext } from "formik";
import { IFormProps } from "../FormAccordionContainer";
import { capitalize } from "@mui/material";
import { AccountPayer, Contact, EnergyType, FormValues } from "../types";
import AutocompleteField from "./AutocompleteField";
import { apiClient } from "src/api/apiService";
import { GasOptions } from "./GasOptions";
import { AppContext } from "src/context/AppContext";
import FormFieldLayout from "./FormFieldLayout";
import { getSmallCase } from "src/components/Premises/helpers";
import MultisiteAgreementAlert from "./MultisiteAgreementAlert";

interface IServiceSectionProps extends IFormProps {
  service: EnergyType;
  isEditing: boolean;
  homeUser?: boolean;
  premiseId?: string | null;
}

export type CollaboratorOption = {
  value: string;
  label: string;
};

const ElecSectionInPremise = (props: IServiceSectionProps) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const {
    context: { user },
  } = React.useContext(AppContext);
  const [collaborators, setCollaborators] = React.useState<
    CollaboratorOption[]
  >([]);
  const { setFieldValue, service, homeUser = false, premiseId } = props;

  const { values } = useFormikContext<FormValues>();
  const {
    assignElectricityRequirements,
    requestingCompany,
    premiseAddress: { state },
    electricityCollaboratorId,
  } = values;
  const showGasOptions = state !== "ACT";
  const disableFuelPayerSelection =
    homeUser &&
    !!premiseId &&
    values.selectedConnections.includes("Gas" as EnergyType);

  React.useEffect(() => {
    const getCollaborators = async () => {
      setLoading(true);
      try {
        const collaboratorsResponse = await apiClient
          .get<Contact[]>(
            `/draft/collaborators/${values.requestingCompany?.id}`
          )
          .then((response) => response.data);

        const collaboratorOptions = collaboratorsResponse?.map((c) => ({
          label: `${c.first_name} ${c.last_name}`,
          value: c.id,
        }));

        const currentUserOption = {
          label: `${user?.first_name} ${user?.last_name}`,
          value: user?.id || "",
        };
        // Add current user to the list of collaborators if they are not already in the list
        // This is used for assignee to display their name when they open the request
        if (electricityCollaboratorId === user?.id) {
          setCollaborators([...collaboratorOptions, currentUserOption]);
        } else {
          setCollaborators(collaboratorOptions);
        }
      } catch (error) {
        console.log(`Fetch collaborators error: ${error}`);
      } finally {
        setLoading(false);
      }
    };
    if (requestingCompany?.id) {
      getCollaborators();
    }
  }, [requestingCompany?.id]);

  return (
    <Stack
      space={{
        xs: "large",
        lg: "xlarge",
      }}
    >
      <Heading variant="h4">{capitalize(service)}</Heading>
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {service === "Gas" && showGasOptions ? <GasOptions /> : null}
        <Columns
          collapseBelow="md"
          space={{
            xs: "xxsmall",
            md: "small",
          }}
          alignY="stretch"
        >
          <Column>
            <FormFieldLayout multipleFields>
              <Field id="account-payer" name="electricityAccountPayer">
                {({
                  field,
                  meta,
                }: {
                  field: FormikProps<FieldProps>;
                  meta: any;
                }) => (
                  <RadioGroup
                    {...field}
                    id="account-payer"
                    name="electricityAccountPayer"
                    label={`Who's responsible for the ${getSmallCase(
                      service
                    )} account?`}
                    error={meta.error}
                    helperText={meta.error}
                    disabled={disableFuelPayerSelection}
                  >
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      flexDirection={{
                        xs: "column",
                        sm: "row",
                      }}
                    >
                      <Radio
                        id="my-company"
                        label={
                          requestingCompany
                            ? requestingCompany.name
                            : homeUser
                            ? "Me/My company"
                            : "My company"
                        }
                        value={AccountPayer.COMPANY}
                        helperText="Will be responsible for any charges until the account is closed."
                        disabled={disableFuelPayerSelection}
                      />
                      <Radio
                        id="customer"
                        label="Customer"
                        value={AccountPayer.SOMEONE_ELSE}
                        helperText="Will be responsible for any charges."
                        disabled={disableFuelPayerSelection}
                      />
                    </Box>
                  </RadioGroup>
                )}
              </Field>
              {values.electricityAccountPayer === AccountPayer.COMPANY &&
                !homeUser && (
                  <MultisiteAgreementAlert service={getSmallCase(service)} />
                )}
            </FormFieldLayout>
          </Column>
        </Columns>
        {!homeUser && (
          <Columns
            collapseBelow="md"
            space={{
              xs: "xxsmall",
              md: "xxlarge",
            }}
            alignY="stretch"
          >
            <Column width="2/3">
              <Stack
                space={{
                  xs: "large",
                  md: "xlarge",
                }}
              >
                <Checkbox
                  id={"electricity.assignRequirements"}
                  label="Assign Electricity requirements"
                  onChange={() => {
                    setFieldValue(
                      "assignElectricityRequirements",
                      !assignElectricityRequirements
                    );
                    setFieldValue("electricityCollaboratorId", "");
                  }}
                  checked={assignElectricityRequirements}
                />
                {assignElectricityRequirements && !homeUser ? (
                  <Field
                    id="electricityCollaborator-autocomplete-field"
                    component={AutocompleteField}
                    name="electricityCollaboratorId"
                    label="Assigned to"
                    options={collaborators}
                    loading={loading}
                  />
                ) : null}
              </Stack>
            </Column>
            {/* HelperText */}
            {assignElectricityRequirements && !homeUser ? (
              <Column width="1/3">
                <Box display="flex" alignItems="stretch" height="full">
                  <Hidden below="md">
                    <Box
                      style={{ borderLeft: "1px solid #E3E3E3" }}
                      paddingRight="medium"
                      height="full"
                    />
                  </Hidden>
                  <Box alignSelf="center">
                    <Text variant="body-small" tone="neutralLight">
                      The person assigned to the electricity request will see a
                      draft in their “Tasks” section. You can reassign the
                      request to someone else at any time, or complete and lodge
                      it yourself.
                    </Text>
                  </Box>
                </Box>
              </Column>
            ) : null}
            {/* End HelperText */}
          </Columns>
        )}
      </Stack>
    </Stack>
  );
};

export default ElecSectionInPremise;
